export interface ReviewTextSegment {
  text: string;
  isFlagged: boolean;
}

interface FlaggedRange {
  start: number;
  end: number;
}

/** Case-insensitive: a phrase the reviewer recased is still the phrase that was rejected. */
const toMatchedRanges = (content: string, phrases: string[]): FlaggedRange[] => {
  const haystack = content.toLowerCase();
  const ranges: FlaggedRange[] = [];

  for (const phrase of phrases) {
    const needle = phrase.trim().toLowerCase();
    if (!needle) {
      continue;
    }

    let searchFrom = 0;
    for (;;) {
      const matchedAt = haystack.indexOf(needle, searchFrom);
      if (matchedAt === -1) {
        break;
      }
      ranges.push({ start: matchedAt, end: matchedAt + needle.length });
      // One past the match, not past its end: 'ana' occurs twice in 'banana', and resuming after
      // the first would leave the second unmarked. The overlap is merged away downstream.
      searchFrom = matchedAt + 1;
    }
  }

  return ranges;
};

/** Two phrases that overlap or touch would otherwise paint two marks with a seam between them. */
const mergeRanges = (ranges: FlaggedRange[]): FlaggedRange[] => {
  const sorted = [...ranges].sort((left, right) => left.start - right.start);

  return sorted.reduce<FlaggedRange[]>((merged, range) => {
    const previous = merged.at(-1);
    if (previous && range.start <= previous.end) {
      previous.end = Math.max(previous.end, range.end);
      return merged;
    }
    return [...merged, { ...range }];
  }, []);
};

/** Recomputed from the text as it stands, so editing a phrase clears its mark as they type. */
export const toFlaggedTextSegments = (content: string, flaggedText?: string[]): ReviewTextSegment[] => {
  if (!content) {
    return [];
  }
  if (!flaggedText?.length) {
    return [{ text: content, isFlagged: false }];
  }

  const ranges = mergeRanges(toMatchedRanges(content, flaggedText));
  if (ranges.length === 0) {
    return [{ text: content, isFlagged: false }];
  }

  const segments: ReviewTextSegment[] = [];
  let cursor = 0;

  for (const { start, end } of ranges) {
    if (start > cursor) {
      segments.push({ text: content.slice(cursor, start), isFlagged: false });
    }
    segments.push({ text: content.slice(start, end), isFlagged: true });
    cursor = end;
  }

  if (cursor < content.length) {
    segments.push({ text: content.slice(cursor), isFlagged: false });
  }

  return segments;
};
